/**
 * A lightweight HTML marker built on google.maps.OverlayView.
 *
 * We use this instead of AdvancedMarkerElement because the latter requires a
 * Cloud Map ID, and the product has none (the mobile app styles its map with
 * runtime JSON and classic markers — see ivy-app). OverlayView lets us render
 * the exact same HTML/CSS marker content with no Map ID and no console warning.
 *
 * The class can only extend google.maps.OverlayView after the Maps JS API has
 * loaded, so we build it lazily and cache it.
 */

export interface HtmlMarkerOptions {
  position: { lat: number; lng: number }
  content: HTMLElement
  map?: any
  title?: string
  onClick?: () => void
}

export interface HtmlMarker {
  setContent(content: HTMLElement, onClick?: () => void): void
  setPosition(position: { lat: number; lng: number }): void
  setMap(map: any | null): void
}

let HtmlMarkerClass: { new (opts: HtmlMarkerOptions): HtmlMarker } | null = null

export function getHtmlMarkerClass(): { new (opts: HtmlMarkerOptions): HtmlMarker } {
  if (HtmlMarkerClass) return HtmlMarkerClass

  const g = window.google

  class HtmlMarkerImpl extends g.maps.OverlayView {
    private position: any
    private container: HTMLDivElement
    private clickHandler?: () => void

    constructor(opts: HtmlMarkerOptions) {
      super()
      this.position = new g.maps.LatLng(opts.position.lat, opts.position.lng)

      this.container = document.createElement('div')
      this.container.style.position = 'absolute'
      this.container.style.transform = 'translate(-50%, -50%)'
      this.container.style.willChange = 'transform'
      this.container.style.cursor = 'pointer'
      if (opts.title) this.container.title = opts.title
      this.container.appendChild(opts.content)

      if (opts.onClick) {
        this.clickHandler = opts.onClick
        this.container.addEventListener('click', opts.onClick)
      }

      if (opts.map) this.setMap(opts.map)
    }

    onAdd() {
      const panes = this.getPanes()
      // overlayMouseTarget receives DOM events (so the marker is clickable).
      panes?.overlayMouseTarget.appendChild(this.container)
    }

    draw() {
      const projection = this.getProjection()
      if (!projection) return
      const point = projection.fromLatLngToDivPixel(this.position)
      if (!point) return
      this.container.style.left = `${point.x}px`
      this.container.style.top = `${point.y}px`
    }

    onRemove() {
      if (this.container.parentNode) {
        this.container.parentNode.removeChild(this.container)
      }
    }

    setContent(content: HTMLElement, onClick?: () => void) {
      if (this.clickHandler) {
        this.container.removeEventListener('click', this.clickHandler)
        this.clickHandler = undefined
      }
      this.container.innerHTML = ''
      this.container.appendChild(content)
      if (onClick) {
        this.clickHandler = onClick
        this.container.addEventListener('click', onClick)
      }
    }

    setPosition(position: { lat: number; lng: number }) {
      this.position = new g.maps.LatLng(position.lat, position.lng)
      this.draw()
    }
  }

  HtmlMarkerClass = HtmlMarkerImpl as unknown as { new (opts: HtmlMarkerOptions): HtmlMarker }
  return HtmlMarkerClass
}
